import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import FormInput from '../../inputs/FormInput';

// NOTE: Refund-method and shipping-paid-by selects stay as raw MUI TextField
// selects — they're enum dropdowns and FormInput's `select` type is wired to
// SelectAutoComplacent which expects FK-style {id,name} options.

/**
 * Generic confirmation dialog for return-state actions.
 * Renders a different set of fields depending on the action.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string}  props.action       One of: approve | reject | receive | complete | refund-complete | cancel.
 * @param {object}  props.returnData   Current return record (used to pre-fill the items payload for "approve").
 * @param {object}  [props.editableQuantities]  itemId -> approvedQty (for the approve action).
 * @param {function} props.onClose
 * @param {function} props.onSubmit    Called with the action's payload object.
 * @param {boolean} props.isSubmitting
 */
const ReturnActionDialog = ({
    open,
    action,
    returnData,
    editableQuantities,
    onClose,
    onSubmit,
    isSubmitting,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [adminNotes, setAdminNotes] = useState('');
    const [refundMethod, setRefundMethod] = useState('original');
    const [shippingPaidBy, setShippingPaidBy] = useState('customer');
    const [reason, setReason] = useState('');
    const [reference, setReference] = useState('');

    // Reset fields whenever the dialog is reopened or the action changes.
    useEffect(() => {
        if (open) {
            setAdminNotes('');
            setRefundMethod('original');
            setShippingPaidBy('customer');
            setReason('');
            setReference('');
        }
    }, [open, action]);

    const config = {
        approve: {
            titleAr: 'الموافقة على المرتجع',
            titleEn: 'Approve Return',
            confirmAr: 'تأكيد الموافقة',
            confirmEn: 'Confirm Approval',
            color: 'success',
        },
        reject: {
            titleAr: 'رفض المرتجع',
            titleEn: 'Reject Return',
            confirmAr: 'تأكيد الرفض',
            confirmEn: 'Confirm Rejection',
            color: 'error',
        },
        receive: {
            titleAr: 'تأكيد استلام المرتجع',
            titleEn: 'Mark as Received',
            confirmAr: 'تأكيد الاستلام',
            confirmEn: 'Mark Received',
            color: 'primary',
        },
        complete: {
            titleAr: 'إنهاء المرتجع',
            titleEn: 'Complete Return',
            confirmAr: 'إنهاء',
            confirmEn: 'Complete',
            color: 'success',
        },
        'refund-complete': {
            titleAr: 'تأكيد إتمام الاسترداد',
            titleEn: 'Mark Refund Complete',
            confirmAr: 'تأكيد',
            confirmEn: 'Mark Complete',
            color: 'success',
        },
        cancel: {
            titleAr: 'إلغاء المرتجع',
            titleEn: 'Cancel Return',
            confirmAr: 'تأكيد الإلغاء',
            confirmEn: 'Confirm Cancellation',
            color: 'warning',
        },
    }[action] || { titleEn: '', titleAr: '', confirmEn: '', confirmAr: '', color: 'primary' };

    const handleSubmit = () => {
        let payload = {};
        switch (action) {
            case 'approve': {
                const items = (returnData?.items || []).map((it) => ({
                    id: it.id,
                    approved_qty: editableQuantities?.[it.id] ?? it.qty ?? it.requested_qty ?? 0,
                }));
                payload = {
                    items,
                    admin_notes: adminNotes || undefined,
                    refund_method: refundMethod,
                    shipping_paid_by: shippingPaidBy,
                };
                break;
            }
            case 'reject':
                payload = { admin_notes: adminNotes };
                break;
            case 'receive':
                payload = { admin_notes: adminNotes || undefined };
                break;
            case 'complete':
                payload = null; // no body
                break;
            case 'refund-complete':
                payload = { reference: reference || undefined };
                break;
            case 'cancel':
                payload = { reason: reason || undefined };
                break;
            default:
                payload = {};
        }
        onSubmit(payload);
    };

    const rejectMissing = action === 'reject' && !adminNotes.trim();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? config.titleAr : config.titleEn}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {action === 'approve' && (
                        <>
                            <TextField
                                select
                                label={isArabic ? 'طريقة الاسترداد' : 'Refund Method'}
                                value={refundMethod}
                                onChange={(e) => setRefundMethod(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="original">
                                    {isArabic ? 'إعادة إلى وسيلة الدفع الأصلية' : 'Original payment method'}
                                </MenuItem>
                                <MenuItem value="store_credit">
                                    {isArabic ? 'رصيد متجر' : 'Store credit'}
                                </MenuItem>
                                <MenuItem value="manual">
                                    {isArabic ? 'يدوي' : 'Manual'}
                                </MenuItem>
                            </TextField>

                            <TextField
                                select
                                label={isArabic ? 'يتحمل تكلفة الشحن العكسي' : 'Return Shipping Paid By'}
                                value={shippingPaidBy}
                                onChange={(e) => setShippingPaidBy(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="customer">
                                    {isArabic ? 'العميل' : 'Customer'}
                                </MenuItem>
                                <MenuItem value="store">
                                    {isArabic ? 'المتجر' : 'Store'}
                                </MenuItem>
                            </TextField>

                            <FormInput
                                type="textarea"
                                name="admin_notes"
                                label={isArabic ? 'ملاحظات إدارية (اختياري)' : 'Admin Notes (optional)'}
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                required={false}
                                textareaRows="100px"
                                placeholder=""
                            />

                            <Typography variant="caption" color="text.secondary">
                                {isArabic
                                    ? 'سيتم استخدام الكميات الموافق عليها التي تم إدخالها في الجدول.'
                                    : 'The approved quantities entered in the items table will be sent.'}
                            </Typography>
                        </>
                    )}

                    {action === 'reject' && (
                        <FormInput
                            type="textarea"
                            name="rejection_reason"
                            label={isArabic ? 'سبب الرفض' : 'Rejection Reason'}
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            error={rejectMissing ? (isArabic ? 'مطلوب' : 'Required') : undefined}
                            required
                            textareaRows="100px"
                            placeholder=""
                        />
                    )}

                    {action === 'receive' && (
                        <FormInput
                            type="textarea"
                            name="receipt_notes"
                            label={isArabic ? 'ملاحظات الاستلام (اختياري)' : 'Receipt Notes (optional)'}
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            required={false}
                            textareaRows="100px"
                            placeholder=""
                        />
                    )}

                    {action === 'complete' && (
                        <Typography variant="body2" color="text.secondary">
                            {isArabic
                                ? 'سيتم إنهاء عملية المرتجع. هل أنت متأكد؟'
                                : 'This will mark the return as completed. Are you sure?'}
                        </Typography>
                    )}

                    {action === 'refund-complete' && (
                        <FormInput
                            type="text"
                            name="refund_reference"
                            label={isArabic ? 'مرجع الاسترداد (اختياري)' : 'Refund Reference (optional)'}
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder={isArabic ? 'مثال: TXN-12345' : 'e.g: TXN-12345'}
                            required={false}
                        />
                    )}

                    {action === 'cancel' && (
                        <FormInput
                            type="textarea"
                            name="cancel_reason"
                            label={isArabic ? 'سبب الإلغاء (اختياري)' : 'Cancellation Reason (optional)'}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            required={false}
                            textareaRows="100px"
                            placeholder=""
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    color={config.color}
                    onClick={handleSubmit}
                    disabled={isSubmitting || rejectMissing}
                >
                    {isSubmitting
                        ? (isArabic ? 'جاري...' : 'Working...')
                        : (isArabic ? config.confirmAr : config.confirmEn)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReturnActionDialog;
